/**
 * Approval Workflow Hooks
 *
 * React Query hooks for image approval functionality.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  getApprovalStats,
  updateApprovalStatus,
  bulkUpdateApproval,
  getSmartModeRecommendation,
  type ApprovalStats,
  type SmartModeRecommendation,
} from '@/lib/api/gallery-v2';

// =============================================================================
// Approval Stats Hook
// =============================================================================

export function useApprovalStats() {
  return useQuery<ApprovalStats>({
    queryKey: ['approval', 'stats'],
    queryFn: getApprovalStats,
    staleTime: 30_000, // 30 seconds
  });
}

// =============================================================================
// Smart Mode Recommendation Hook
// =============================================================================

export function useSmartModeRecommendation() {
  return useQuery<SmartModeRecommendation>({
    queryKey: ['approval', 'smart-mode'],
    queryFn: getSmartModeRecommendation,
    staleTime: 60_000, // 1 minute
  });
}

// =============================================================================
// Approval Actions Hook
// =============================================================================

export function useApprovalActions() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const updateSingleMutation = useMutation({
    mutationFn: ({
      imageId,
      action,
      notes,
    }: {
      imageId: string;
      action: 'approve' | 'reject' | 'skip' | 'reset';
      notes?: string;
    }) => updateApprovalStatus(imageId, action, notes),
    onSuccess: (_, { action }) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'search'] });
      queryClient.invalidateQueries({ queryKey: ['approval', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['approval', 'smart-mode'] });

      // Show success toast
      if (action === 'approve') {
        toast.success(t('toast.success.imageApproved'));
      } else if (action === 'reject') {
        toast.success(t('toast.success.imageRejected'));
      } else if (action === 'skip') {
        toast.info(t('toast.info.imageSkipped'));
      }
    },
    onError: (error) => {
      console.error('Approval update failed:', error);
      toast.error(t('toast.error.approvalAction'));
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: ({
      imageIds,
      action,
      notes,
    }: {
      imageIds: string[];
      action: 'approve' | 'reject';
      notes?: string;
    }) => bulkUpdateApproval(imageIds, action, notes),
    onSuccess: (result, { action: _action }) => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'search'] });
      queryClient.invalidateQueries({ queryKey: ['approval', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['approval', 'smart-mode'] });

      // Show success toast
      toast.success(t('toast.success.bulkApproved', { count: result.success_count }));

      if (result.failed_count > 0) {
        toast.warning(t('toast.error.partialFailed', { count: result.failed_count }));
      }
    },
    onError: (error) => {
      console.error('Bulk approval update failed:', error);
      toast.error(t('toast.error.bulkApproval'));
    },
  });

  return {
    updateSingle: updateSingleMutation.mutate,
    updateSingleAsync: updateSingleMutation.mutateAsync,
    bulkUpdate: bulkUpdateMutation.mutate,
    bulkUpdateAsync: bulkUpdateMutation.mutateAsync,
    isUpdatingSingle: updateSingleMutation.isPending,
    isUpdatingBulk: bulkUpdateMutation.isPending,
  };
}
